import React from 'react';
import styled from 'styled-components';

import {
  CARD_TYPES,
  CARD_WRAPPER_TYPES,
  CONTENTFUL_COMPONENT,
  CONTENTFUL_SLUGS,
} from '../../shared/constants';
import { PageType } from '../../shared/constants/Contentful.constants';
import {
  IArticle,
  ICard,
  ICardWrapper,
  IContactForm,
  IContentfulLink,
  IContentfulLinkWithModal,
  IFormFieldset,
  IImage,
  IParentPage,
  Tag,
} from '../../types/shared';

import ContentSectionHeader from '../../components/atoms/content-section-header/ContentSectionHeader';

import AnchorCard from '../../components/molecules/anchor-card/AnchorCard';
import CardWrapper from '../../components/molecules/card-wrapper/CardWrapper';
import ContentHighlight from '../../components/molecules/content-highlight/ContentHighlight';
import Disclaimer from '../../components/molecules/disclaimer/Disclaimer';
import FaqWrapper from '../../components/molecules/faq-wrapper/FaqWrapper';
import FeaturedArticlesWrapper from '../../components/molecules/featured-articles-wrapper/FeaturedArticlesWrapper';
import FeedbackCard from '../../components/molecules/feedback-card/FeedbackCard';
import FormWrapper from '../../components/molecules/form-wrapper/FormWrapper';
import GatedContentWrapper from '../../components/molecules/gated-content-wrapper/GatedContentWrapper';
import GatedDownloadCard from '../../components/molecules/gated-download-card/GatedDownloadCard';
import GenericCard from '../../components/molecules/generic-card/GenericCard';
import HighlightCard from '../../components/molecules/highlight-card/HighlightCard';
import IconListWrapper from '../../components/molecules/icon-list-wrapper/IconListWrapper';
import ImageOrVideoCard from '../../components/molecules/image-or-video-card/ImageOrVideoCard';
import ImageRow from '../../components/molecules/image-row/ImageRow';
import LinkCard from '../../components/molecules/link-card/LinkCard';
import LinkTilesWrapper from '../../components/molecules/link-tiles-wrapper/LinkTilesWrapper';
import PreviewCard from '../../components/molecules/preview-card/PreviewCard';
import ProductFeaturesWrapper from '../../components/molecules/product-features-wrapper/ProductFeaturesWrapper';
import Promovisual from '../../components/molecules/promovisual/Promovisual';
import QuoteItem from '../../components/molecules/quote-item/QuoteItem';
import RowOfImagesWrapper from '../../components/molecules/row-of-images-wrapper/RowOfImagesWrapper';
import Step from '../../components/molecules/step/Step';
import StoryCard from '../../components/molecules/story-card/StoryCard';
import Tile from '../../components/molecules/tile/Tile';
import Tip from '../../components/molecules/tip/Tip';
import UspItem from '../../components/molecules/usp-item/UspItem';
import VideoCTA from '../../components/molecules/video-cta/VideoCTA';
import Calculator from '../../components/organisms/calculator/Calculator';
import { CalculatorData } from '../../components/organisms/calculator/Calculator.types';
import FeedbackForm from '../../components/organisms/feedback-form/FeedbackForm';
import LeadForm from '../../components/organisms/lead-form/LeadForm';
import { ContentfulGenericPage } from '../../components/pages/generic-page/GenericPage.types';
import { TagTypes } from '../../tokens';
import { getTagsByPriority, getTagsByTypes } from '../tags';

import ReactMarkdownExtended from './ReactMarkdownExtended';

export const hasContactFormInBody = (
  bodyItems: (ICard | ICardWrapper | IContactForm)[]
): boolean => {
  return bodyItems.some((bodyItem) => {
    if ('cards' in bodyItem && bodyItem.cards) {
      // Check if item is a CardWrapper and cards are not null
      return bodyItem.cards.some(
        (card) => card?.__typename === CONTENTFUL_COMPONENT.formContactForm
      );
    }
    // Otherwise, check the bodyItem itself
    return bodyItem?.__typename === CONTENTFUL_COMPONENT.formContactForm;
  });
};

export const renderItem = (
  card: ICard,
  key: number,
  bodyType: string,
  pageName: string,
  tags?: Tag[],
  numberOfSiblings?: number
) => {
  const { type } = card;

  if (type === CARD_TYPES.tip) {
    return (
      <Tip
        key={`${card.title}-${key}`}
        image={card.image as IImage}
        title={card.title}
        primaryLink={card.primaryLink as IContentfulLink}
        anchorName={card?.anchorName}
      />
    );
  }
  if (type === CARD_TYPES.tile) {
    return (
      <Tile
        image={card.image as IImage}
        key={`${card.title}-${key}`}
        title={card.title}
        primaryLink={card.primaryLink as IContentfulLink}
        elementPosition={key}
      >
        {card.body && (
          <ReactMarkdownExtended componentType={CARD_TYPES.tile}>
            {card.body.childMarkdownRemark.rawMarkdownBody}
          </ReactMarkdownExtended>
        )}
      </Tile>
    );
  }
  if (type === CARD_TYPES.highlight) {
    return (
      <HighlightCard
        key={`${card.title}-${key}`}
        image={card.image as IImage}
        primaryLink={card.primaryLink as IContentfulLinkWithModal}
        secondaryLink={card.secondaryLink as IContentfulLinkWithModal}
        title={card.title}
        productName={pageName}
      >
        <ReactMarkdownExtended componentType={CARD_TYPES.highlight}>
          {card.body.childMarkdownRemark.rawMarkdownBody}
        </ReactMarkdownExtended>
      </HighlightCard>
    );
  }
  if (type === CARD_TYPES.usp || type === CARD_TYPES.uspNumerical) {
    return (
      <UspItem
        title={card.title}
        image={card.image as IImage}
        key={`${card.type}-${key}`}
        primaryLink={card.primaryLink as IContentfulLink}
        uspType={card.type}
      >
        {card.body && (
          <ReactMarkdownExtended componentType={type}>
            {card.body.childMarkdownRemark.rawMarkdownBody}
          </ReactMarkdownExtended>
        )}
      </UspItem>
    );
  }
  if (type === CARD_TYPES.imageRow) {
    return (
      <ImageRow image={card.image as IImage} key={`${card.type}-${key}`} />
    );
  }

  if (type === CARD_TYPES.contentHighlight) {
    return (
      <ContentHighlight
        id={type}
        index={key}
        title={card?.title}
        subtitle={card?.subtitle}
        image={card?.image as IImage}
        primaryLink={card?.primaryLink as IContentfulLinkWithModal}
        secondaryLink={card?.secondaryLink as IContentfulLinkWithModal}
        key={`${card.title}-${key}`}
      >
        <ReactMarkdownExtended componentType={type}>
          {card.body.childMarkdownRemark.rawMarkdownBody}
        </ReactMarkdownExtended>
      </ContentHighlight>
    );
  }

  if (type === CARD_TYPES.anchor || type === CARD_TYPES.embeddedVideoCard) {
    return (
      <AnchorCard
        id={type}
        index={key}
        alignment={card.alignment}
        title={card.title}
        image={card.image as IImage}
        primaryLink={card.primaryLink as IContentfulLinkWithModal}
        secondaryLink={card.secondaryLink as IContentfulLinkWithModal}
        videoLink={
          type === CARD_TYPES.embeddedVideoCard
            ? (card.subtitle as string)
            : undefined
        }
        key={`${card.title}-${key}`}
      >
        <ReactMarkdownExtended componentType={type}>
          {card.body.childMarkdownRemark.rawMarkdownBody}
        </ReactMarkdownExtended>
      </AnchorCard>
    );
  }
  if (type === CARD_TYPES.imageOrVideoOnly) {
    return (
      <ImageOrVideoCard
        key={`${card.title}-${key}`}
        title={card.title}
        image={card.image as IImage}
      />
    );
  }
  if (type === CARD_TYPES.animatedVideoCard) {
    return (
      <VideoCTA
        key={`${card.title}-${key}`}
        title={card.title}
        subtitle={card.subtitle as string}
        video={card.image}
        label={card.primaryLink?.label as string}
        to={card.primaryLink?.linkedPage}
      />
    );
  }
  if (type === CARD_TYPES.step) {
    return (
      <Step
        key={`${card.title}-${key}`}
        title={card?.title}
        index={key}
        type={bodyType}
        primaryLink={card?.primaryLink}
        secondaryLink={card?.secondaryLink}
      >
        <ReactMarkdownExtended componentType={CARD_TYPES.step}>
          {card.body.childMarkdownRemark.rawMarkdownBody}
        </ReactMarkdownExtended>
      </Step>
    );
  }
  if (type === CARD_TYPES.storyCard) {
    return (
      <StoryCard
        key={`${card.title}-${key}`}
        title={card.title}
        secondaryLink={card.secondaryLink as IContentfulLink}
      >
        <ReactMarkdownExtended componentType={CARD_TYPES.storyCard}>
          {card.body.childMarkdownRemark.rawMarkdownBody}
        </ReactMarkdownExtended>
      </StoryCard>
    );
  }
  if (type === CARD_TYPES.linkCard) {
    return (
      <LinkCard
        key={`${card.title}-${key}`}
        title={card.title}
        secondaryLink={card.secondaryLink as IContentfulLink}
        image={card.image}
      >
        <ReactMarkdownExtended
          componentType={CARD_TYPES.linkCard}
          linkTarget="_self"
        >
          {card.body.childMarkdownRemark.rawMarkdownBody}
        </ReactMarkdownExtended>
      </LinkCard>
    );
  }
  if (type === CARD_TYPES.gatedDownloadCard) {
    return (
      <GatedDownloadCard
        key={`${card.title}-${key}`}
        title={card.title}
        primaryLink={card.primaryLink as IContentfulLink}
        image={card.image}
        tags={tags}
      >
        <ReactMarkdownExtended
          componentType={CARD_TYPES.gatedDownloadCard}
          linkTarget="_self"
        >
          {card.body.childMarkdownRemark.rawMarkdownBody}
        </ReactMarkdownExtended>
      </GatedDownloadCard>
    );
  }
  if (
    (card as unknown as ContentfulGenericPage).__typename ===
      CONTENTFUL_COMPONENT.genericPage &&
    (card as unknown as ContentfulGenericPage).pageType ===
      PageType.GATED_CONTACT
  ) {
    const headerCardData = (card as unknown as ContentfulGenericPage)?.header;
    const overviewCardData = (card as unknown as ContentfulGenericPage)
      ?.overviewCard;
    const cardData = overviewCardData || headerCardData;

    return (
      <GatedDownloadCard
        key={`${cardData.title}-${key}`}
        title={cardData.title}
        primaryLink={cardData.primaryLink as IContentfulLink}
        pageGenericLink={{
          slug: (card as unknown as ContentfulGenericPage).slug,
          parentPage: (card as unknown as ContentfulGenericPage)?.parentPage,
        }}
        image={cardData?.image}
        subtitle={!cardData?.body ? cardData?.subtitle : ''}
        tags={(card as unknown as ContentfulGenericPage)?.tags}
      >
        {cardData?.body && (
          <ReactMarkdownExtended
            componentType={CARD_TYPES.gatedDownloadCard}
            linkTarget="_self"
          >
            {cardData.body.childMarkdownRemark?.rawMarkdownBody}
          </ReactMarkdownExtended>
        )}
      </GatedDownloadCard>
    );
  }
  if (type === CARD_TYPES.quote) {
    return (
      <QuoteItem
        key={`${card.title}-${key}`}
        title={card.title}
        subtitle={card.subtitle as string}
        image={card.image as IImage}
      >
        <ReactMarkdownExtended componentType={CARD_TYPES.quote}>
          {card.body.childMarkdownRemark.rawMarkdownBody}
        </ReactMarkdownExtended>
      </QuoteItem>
    );
  }
  if (type === CARD_TYPES.disclaimer) {
    return (
      <DisclaimerWrapper key={`${card.title}-${key}`}>
        <Disclaimer
          key={`${card.title}-${key}`}
          title={card.title}
          anchorName={card?.anchorName}
        >
          <ReactMarkdownExtended componentType={CARD_TYPES.disclaimer}>
            {card.body.childMarkdownRemark.rawMarkdownBody}
          </ReactMarkdownExtended>
        </Disclaimer>
      </DisclaimerWrapper>
    );
  }
  if (type === CARD_TYPES.promovisual) {
    return (
      <Promovisual
        key={`${card.title}-${key}`}
        title={card.title}
        subtitle={card.subtitle as string}
        reverse={key % 2 !== 0}
        secondaryLink={card?.secondaryLink as IContentfulLinkWithModal}
        thumbnail={card?.image as IImage}
        primaryLink={card?.primaryLink as IContentfulLinkWithModal}
        productName={pageName}
      >
        <ReactMarkdownExtended componentType={CARD_TYPES.promovisual}>
          {card.body.childMarkdownRemark.rawMarkdownBody}
        </ReactMarkdownExtended>
      </Promovisual>
    );
  }
  if (type === CARD_TYPES.previewCard) {
    return (
      <PreviewCard
        key={`${card.title}-${key}`}
        index={key}
        tags={tags}
        image={card.image?.gatsbyImageData || card.image?.file?.url}
        title={card.title}
        ctaLinkPrimary={card.primaryLink}
        ctaLinkSecondary={card.secondaryLink}
        numberOfSiblings={numberOfSiblings}
      >
        <ReactMarkdownExtended componentType={CARD_TYPES.previewCard}>
          {card.body?.childMarkdownRemark.rawMarkdownBody}
        </ReactMarkdownExtended>
      </PreviewCard>
    );
  }
  if (type === CARD_TYPES.generic) {
    return (
      <GenericCard
        key={`${card.title}-${key}`}
        title={card.title}
        subtitle={card.subtitle}
        body={card.body}
        primaryLink={card?.primaryLink}
        secondaryLink={card?.secondaryLink}
      />
    );
  }
};

export const renderBodyItem = (
  bodyItem: ICard | ICardWrapper,
  key: number,
  pageType: string,
  tags: Tag[],
  slug: string,
  parentPage?: IParentPage,
  pageName = ''
) => {
  const optionalTrackingName =
    parentPage?.slug === CONTENTFUL_SLUGS.home
      ? slug
      : slug && parentPage?.slug
      ? `${parentPage?.slug} ${slug}`
      : parentPage?.slug;
  const filteredTags = getTagsByTypes(tags || [], [
    TagTypes.SECTOR,
    TagTypes.CATEGORY,
  ]);

  const filteredTagsByPriority = getTagsByPriority(tags || [], [
    TagTypes.CATEGORY,
    TagTypes.TOPIC,
    TagTypes.SECTOR,
    TagTypes.SEGMENT,
  ]);

  if ((bodyItem as unknown as IContactForm)?.formInputs && !bodyItem.type) {
    return (
      <FormWrapper
        key={`${bodyItem.title}-${key}`}
        anchorName={bodyItem.anchorName}
        cardNumber={key}
      >
        <LeadForm
          pageType={pageType}
          cardType={bodyItem.type}
          {...bodyItem}
          productName={optionalTrackingName || bodyItem.name || ''}
        />
      </FormWrapper>
    );
  }

  if (
    (bodyItem as unknown as CalculatorData).__typename ===
    CONTENTFUL_COMPONENT.calculator
  ) {
    const calculatorData = bodyItem as unknown as CalculatorData;

    return (
      <Calculator
        {...calculatorData}
        trackingName={pageName}
        anchorName={bodyItem.anchorName}
        key={key}
      />
    );
  }

  if ((bodyItem as ICardWrapper)?.cards) {
    if (bodyItem.type === CARD_WRAPPER_TYPES.iconListWrapper) {
      return (
        <IconListWrapper
          key={key}
          title={bodyItem.title}
          listItems={(bodyItem as ICardWrapper).cards as ICard[]}
        />
      );
    }
    if (bodyItem.type === CARD_WRAPPER_TYPES.linkTilesWrapper) {
      return (
        <LinkTilesWrapper
          key={key}
          linkTiles={(bodyItem as ICardWrapper).cards as ICard[]}
        />
      );
    }
    if (bodyItem.type === CARD_WRAPPER_TYPES.feedbackCardWrapper) {
      return (
        <FeedbackCard key={key} title={bodyItem.title}>
          <FeedbackForm
            id={bodyItem.id || 'dpkn-feedback-form-v1'}
            formTitle={bodyItem.title}
            fieldset={(bodyItem as ICardWrapper).cards as IFormFieldset[]}
            successImage={bodyItem.image?.file?.url}
            pageType={pageType}
            currentPage={
              parentPage?.slug === CONTENTFUL_SLUGS.home
                ? slug
                : `${parentPage?.slug}/${slug}`
            }
            productName={optionalTrackingName}
          />
        </FeedbackCard>
      );
    }
    if (bodyItem.type === CARD_WRAPPER_TYPES.faqWrapper) {
      return (
        <FaqWrapper
          key={`${bodyItem.title}-${key}`}
          title={bodyItem.title}
          subtitle={bodyItem.subtitle as string}
          cards={(bodyItem as ICardWrapper).cards as ICard[]}
          anchorName={bodyItem.anchorName}
        />
      );
    }
    if (bodyItem.type === CARD_WRAPPER_TYPES.featuredArticlesWrapper) {
      return (
        <FeaturedArticlesWrapper
          key={`${bodyItem.title}-${key}`}
          articles={(bodyItem as ICardWrapper).cards as IArticle[]}
          anchorName={bodyItem.anchorName}
        >
          <ContentSectionHeader
            title={bodyItem.title || 'Meer over'}
            tags={filteredTags}
          />
        </FeaturedArticlesWrapper>
      );
    }
    if (bodyItem.type === CARD_WRAPPER_TYPES.formWrapper) {
      return (
        <FormWrapper
          key={`${bodyItem.title}-${key}`}
          anchorName={bodyItem.anchorName}
          body={bodyItem.body}
          cardNumber={key}
        >
          <LeadForm
            pageType={pageType}
            cardType={bodyItem.type}
            {...((bodyItem as ICardWrapper).cards as ICard[])[0]}
            productName={optionalTrackingName}
          />
        </FormWrapper>
      );
    }
    if (bodyItem.type === CARD_WRAPPER_TYPES.gatedContentWrapper) {
      return (
        <GatedContentWrapper
          title={bodyItem.title}
          image={bodyItem.image as IImage}
          key={`${bodyItem.title}-${key}`}
          anchorName={bodyItem.anchorName}
        >
          <LeadForm
            pageType={pageType}
            cardType={bodyItem.type}
            {...((bodyItem as ICardWrapper).cards as ICard[])[0]}
            productName={optionalTrackingName}
          />
        </GatedContentWrapper>
      );
    }
    if (bodyItem.type === CARD_WRAPPER_TYPES.ungatedContentWrapper) {
      return (
        <FormWrapper
          key={`${bodyItem.title}-${key}`}
          anchorName={bodyItem.anchorName}
          body={bodyItem.body}
          cardNumber={key}
        >
          {(bodyItem as ICardWrapper).cards.map((cardItem, index) =>
            renderItem(
              cardItem as ICard,
              index,
              bodyItem.type,
              pageName,
              (bodyItem as ICardWrapper).cards.length
            )
          )}
        </FormWrapper>
      );
    }
    if (bodyItem.type === CARD_WRAPPER_TYPES.productFeaturesWrapper) {
      return (
        <ProductFeaturesWrapper
          key={`${bodyItem.title}-${key}`}
          title={bodyItem.title}
          subtitle={bodyItem.subtitle as string}
          features={(bodyItem as ICardWrapper).cards as ICard[]}
          anchorName={bodyItem.anchorName}
        />
      );
    }
    if (bodyItem.type === CARD_WRAPPER_TYPES.rowOfImagesWrapper) {
      return (
        <RowOfImagesWrapper
          key={`${bodyItem.title}-${key}`}
          type={bodyItem.type}
          title={bodyItem.title}
          subtitle={bodyItem.subtitle as string}
          anchorName={bodyItem.anchorName}
        >
          {(bodyItem as ICardWrapper).cards.map((cardItem, index) =>
            renderItem(
              cardItem as ICard,
              index,
              bodyItem.type,
              pageName,
              filteredTagsByPriority,
              (bodyItem as ICardWrapper).cards.length
            )
          )}
        </RowOfImagesWrapper>
      );
    }

    return (
      <CardWrapper
        key={`${bodyItem.title}-${key}`}
        type={bodyItem.type}
        title={bodyItem.title}
        subtitle={bodyItem.subtitle as string}
        description={bodyItem?.body}
        anchorName={bodyItem.anchorName}
      >
        {(bodyItem as ICardWrapper).cards.map((cardItem, index) =>
          renderItem(
            cardItem as ICard,
            index,
            bodyItem.type,
            pageName,
            filteredTagsByPriority,
            (bodyItem as ICardWrapper).cards.length
          )
        )}
      </CardWrapper>
    );
  }

  return renderItem(
    bodyItem as ICard,
    key,
    bodyItem.type,
    pageName,
    filteredTagsByPriority
  );
};

const DisclaimerWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: inherit;
`;
